import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, RotateCcw, HelpCircle, Trophy, Loader2, ArrowRight, Timer, Sparkles, X } from "lucide-react";
import {
  ROWS,
  COLS,
  SPECIAL,
  createBoard,
  trySwap,
  cloneBoard,
  areAdjacent,
  typesForLevel,
  thresholdForLevel,
  timeForLevel,
} from "@/lib/beematch";

/* Bee-garden piece palette (3D gem look via radial gradients + a themed glyph). */
const PIECES = [
  { light: "#FDE68A", color: "#F59E0B", edge: "#B45309", glyph: "🍯" }, // honey
  { light: "#DDD6FE", color: "#8B5CF6", edge: "#6D28D9", glyph: "🐝" }, // bee (purple, distinct from honey)
  { light: "#FBCFE8", color: "#EC4899", edge: "#9D174D", glyph: "🌸" }, // blossom
  { light: "#BBF7D0", color: "#22C55E", edge: "#15803D", glyph: "🍃" }, // leaf
  { light: "#BFDBFE", color: "#3B82F6", edge: "#1D4ED8", glyph: "💧" }, // nectar
  { light: "#FED7AA", color: "#F97316", edge: "#C2410C", glyph: "🌻" }, // sunflower
  { light: "#FECACA", color: "#EF4444", edge: "#B91C1C", glyph: "🍓" }, // berry (red)
];

const CELL = 46;
const GAP = 4;
const boardPx = COLS * CELL + (COLS - 1) * GAP;

/* ── honeycomb background (real SVG hex grid) ─────────────────────────────── */
function hexPoints(cx, cy, R) {
  const pts = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 90);
    pts.push(`${(cx + R * Math.cos(a)).toFixed(1)},${(cy + R * Math.sin(a)).toFixed(1)}`);
  }
  return pts.join(" ");
}
function useHexes(w, h, R) {
  return useMemo(() => {
    const out = [];
    const hw = Math.sqrt(3) * R;
    const vs = 1.5 * R;
    for (let j = -1; j * vs < h + R; j++) {
      for (let i = -1; i * hw < w + hw; i++) {
        out.push(hexPoints(i * hw + (j % 2 ? hw / 2 : 0), j * vs, R));
      }
    }
    return out;
  }, [w, h, R]);
}

/* ── a single 3D gem (with optional special overlay) ─────────────────────── */
function Gem({ type, special }) {
  const p = PIECES[type] || PIECES[0];
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: "block" }}>
      <rect x="9" y="9" width="82" height="82" rx="24" fill={`url(#bm-grad-${type})`} stroke={p.edge} strokeWidth="3" filter="url(#bm-shadow)" />
      <ellipse cx="38" cy="30" rx="20" ry="11" fill="rgba(255,255,255,0.6)" />
      <text x="50" y="56" textAnchor="middle" dominantBaseline="middle" fontSize="40">{p.glyph}</text>
      {special === SPECIAL.ROW && (
        <g>
          <rect x="6" y="6" width="88" height="88" rx="26" fill="none" stroke="#fff" strokeWidth="3" strokeDasharray="6 5" opacity="0.9" />
          <rect x="9" y="44" width="82" height="12" fill="rgba(255,255,255,0.55)" />
          <path d="M20 50 L30 42 L30 58 Z M80 50 L70 42 L70 58 Z" fill="#fff" />
        </g>
      )}
      {special === SPECIAL.COL && (
        <g>
          <rect x="6" y="6" width="88" height="88" rx="26" fill="none" stroke="#fff" strokeWidth="3" strokeDasharray="6 5" opacity="0.9" />
          <rect x="44" y="9" width="12" height="82" fill="rgba(255,255,255,0.55)" />
          <path d="M50 20 L42 30 L58 30 Z M50 80 L42 70 L58 70 Z" fill="#fff" />
        </g>
      )}
      {special === SPECIAL.CROSS && (
        <g>
          <rect x="6" y="6" width="88" height="88" rx="26" fill="none" stroke="#fff" strokeWidth="3" strokeDasharray="6 5" opacity="0.95" />
          <rect x="9" y="44" width="82" height="12" fill="rgba(255,255,255,0.62)" />
          <rect x="44" y="9" width="12" height="82" fill="rgba(255,255,255,0.62)" />
        </g>
      )}
      {special === SPECIAL.BOMB && (
        <g>
          <circle cx="50" cy="50" r="44" fill="none" stroke="url(#bm-rainbow)" strokeWidth="5" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
            const rad = (Math.PI / 180) * a;
            return <line key={a} x1={50 + 30 * Math.cos(rad)} y1={50 + 30 * Math.sin(rad)} x2={50 + 44 * Math.cos(rad)} y2={50 + 44 * Math.sin(rad)} stroke="#fff" strokeWidth="3" strokeLinecap="round" opacity="0.85" />;
          })}
        </g>
      )}
    </svg>
  );
}

function SharedDefs() {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
      <defs>
        {PIECES.map((p, i) => (
          <radialGradient key={i} id={`bm-grad-${i}`} cx="38%" cy="30%" r="75%">
            <stop offset="0%" stopColor={p.light} />
            <stop offset="60%" stopColor={p.color} />
            <stop offset="100%" stopColor={p.edge} />
          </radialGradient>
        ))}
        <linearGradient id="bm-rainbow" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f43f5e" />
          <stop offset="33%" stopColor="#eab308" />
          <stop offset="66%" stopColor="#22c55e" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
        <filter id="bm-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2.5" stdDeviation="2" floodColor="rgba(0,0,0,0.28)" />
        </filter>
      </defs>
    </svg>
  );
}

export default function BeeMatch() {
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState("casual"); // 'casual' | 'timed'
  const [level, setLevel] = useState(1);
  const [bestLevel, setBestLevel] = useState(1);
  const [wins, setWins] = useState(0);
  const [highScore, setHighScore] = useState(0);

  const [board, setBoard] = useState([]);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState("playing"); // 'playing' | 'won' | 'lost'
  const [timeLeft, setTimeLeft] = useState(60);
  const [showHelp, setShowHelp] = useState(false);

  const hexes = useHexes(boardPx, boardPx, 24);
  const threshold = thresholdForLevel(level);
  const types = typesForLevel(level);

  // Per-cell version counters: a cell re-mounts (and replays the drop-in
  // animation) only when its version changes — i.e. when its piece actually
  // moved, cleared or spawned.
  const genRef = useRef(1);
  const versionsRef = useRef(Array.from({ length: ROWS }, () => Array(COLS).fill(0)));

  const startLevel = useCallback((lvl, md, keep = []) => {
    genRef.current += 1;
    versionsRef.current = Array.from({ length: ROWS }, () => Array(COLS).fill(genRef.current));
    setLevel(lvl);
    setMode(md);
    setBoard(createBoard(lvl, Math.random, keep));
    setScore(0);
    setSelected(null);
    setStatus("playing");
    setTimeLeft(timeForLevel(lvl));
  }, []);

  /* ── load progress ──────────────────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    api
      .get("/games/beematch")
      .then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        setBestLevel(d.best_level || 1);
        setWins(d.wins || 0);
        setHighScore(d.high_score || 0);
        startLevel(d.level || 1, d.mode === "timed" ? "timed" : "casual");
      })
      .catch(() => startLevel(1, "casual"))
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [startLevel]);

  /* ── persist (debounced) ────────────────────────────────────────────────── */
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!loaded) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/games/beematch", { level, best_level: Math.max(bestLevel, level), wins, high_score: highScore, mode }).catch(() => {});
    }, 500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [loaded, level, bestLevel, wins, highScore, mode]);

  /* ── countdown (timed mode) ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!loaded || mode !== "timed" || status !== "playing") return undefined;
    const id = setInterval(() => setTimeLeft((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(id);
  }, [loaded, mode, status, level]);

  const win = useCallback(
    (finalScore) => {
      setStatus("won");
      setWins((w) => w + 1);
      setBestLevel((b) => Math.max(b, level + 1));
      setHighScore((h) => Math.max(h, finalScore));
    },
    [level],
  );

  /* time-up resolves the round */
  useEffect(() => {
    if (timeLeft === 0 && mode === "timed" && status === "playing") {
      if (score >= threshold) win(score);
      else setStatus("lost");
    }
  }, [timeLeft, mode, status, score, threshold, win]);

  /* ── interaction ────────────────────────────────────────────────────────── */
  const attemptSwap = useCallback(
    (a, b) => {
      if (status !== "playing") return;
      const nb = cloneBoard(board);
      const res = trySwap(nb, a, b, types);
      if (!res.ok) return;
      const V = versionsRef.current;
      for (let r2 = 0; r2 < ROWS; r2++)
        for (let c2 = 0; c2 < COLS; c2++) {
          const o = board[r2][c2];
          const n = nb[r2][c2];
          if (!o || o.t !== n.t || o.s !== n.s) V[r2][c2] += 1;
        }
      setBoard(nb);
      setSelected(null);
      const ns = score + res.points;
      setScore(ns);
      if (res.specials > 0) toast.success("Power piece! ✨");
      if (ns >= threshold) win(ns);
    },
    [status, board, types, score, threshold, win],
  );

  // Tap flow: first tap selects, tapping an adjacent piece swaps.
  const tapCell = useCallback(
    (r, c) => {
      if (status !== "playing") return;
      const cur = { r, c };
      if (!selected) return setSelected(cur);
      if (selected.r === r && selected.c === c) return setSelected(null);
      if (!areAdjacent(selected, cur)) return setSelected(cur);
      attemptSwap(selected, cur);
    },
    [status, selected, attemptSwap],
  );

  // Drag flow: press a piece and drag toward a neighbour to swap.
  const gridRef = useRef(null);
  const dragRef = useRef(null);
  const onPointerDown = useCallback(
    (r, c, e) => {
      if (status !== "playing") return;
      dragRef.current = { r, c, x0: e.clientX, y0: e.clientY, moved: false };
      try {
        gridRef.current?.setPointerCapture(e.pointerId);
      } catch {
        /* pointer capture unsupported */
      }
    },
    [status],
  );
  const onPointerMove = useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d || d.moved) return;
      const dx = e.clientX - d.x0;
      const dy = e.clientY - d.y0;
      if (Math.abs(dx) < 16 && Math.abs(dy) < 16) return; // below drag threshold
      dragRef.current = null; // consume this gesture
      let nr = d.r;
      let nc = d.c;
      if (Math.abs(dx) >= Math.abs(dy)) nc += dx > 0 ? 1 : -1;
      else nr += dy > 0 ? 1 : -1;
      setSelected(null);
      if (nr >= 0 && nr < ROWS && nc >= 0 && nc < COLS) attemptSwap({ r: d.r, c: d.c }, { r: nr, c: nc });
    },
    [attemptSwap],
  );
  const onPointerUp = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d && !d.moved) tapCell(d.r, d.c); // no drag → treat as a tap
  }, [tapCell]);

  const resetLevel = useCallback(() => startLevel(level, mode), [startLevel, level, mode]);
  const nextLevel = useCallback(() => {
    const nl = level + 1;
    // Carry any surviving power pieces to the next level, in place.
    const carried = [];
    for (let r = 0; r < ROWS; r++)
      for (let c = 0; c < COLS; c++) {
        const cell = board[r][c];
        if (cell && cell.s !== SPECIAL.NONE) carried.push({ r, c, s: cell.s, t: cell.t });
      }
    startLevel(nl, mode, carried);
    toast.success(
      carried.length
        ? `Level ${nl} · ${carried.length} power piece${carried.length > 1 ? "s" : ""} carried over ✨`
        : `Level ${nl}`,
    );
  }, [startLevel, level, mode, board]);
  const switchMode = useCallback((md) => { if (md !== mode) startLevel(level, md); }, [startLevel, level, mode]);

  if (!loaded) {
    return (
      <div className="grid place-items-center py-24 text-stone-400">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const pct = Math.min(100, Math.round((score / threshold) * 100));

  return (
    <div className="space-y-5" data-testid="beematch-page">
      <style>{`@keyframes bm-drop{0%{transform:translateY(-34px) scale(.8);opacity:0}70%{opacity:1}100%{transform:translateY(0) scale(1);opacity:1}}`}</style>
      <SharedDefs />

      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/app/games" className="text-stone-500 hover:text-amber-600" data-testid="beematch-back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">🐝 Bee Match</h1>
            <p className="text-sm text-stone-500">
              Level <span className="font-semibold text-stone-700">{level}</span> · reach{" "}
              <span className="font-semibold text-amber-700">{threshold.toLocaleString()}</span> pts
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-stone-200 bg-white p-0.5 text-sm">
            {["casual", "timed"].map((m) => (
              <button
                key={m}
                onClick={() => switchMode(m)}
                data-testid={`beematch-mode-${m}`}
                className={`rounded-full px-3 py-1 capitalize transition-colors ${mode === m ? "bg-amber-500 text-white" : "text-stone-500 hover:text-stone-700"}`}
              >
                {m === "timed" && <Timer className="mr-1 inline h-3.5 w-3.5" />}
                {m}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={resetLevel} data-testid="beematch-reset">
            <RotateCcw className="mr-1 h-4 w-4" /> New board
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)} data-testid="beematch-help">
            <HelpCircle className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* score + timer */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-[220px] flex-1">
          <div className="mb-1 flex justify-between text-sm">
            <span className="text-stone-500">Score</span>
            <span className="font-semibold tabular-nums text-stone-800">
              {score.toLocaleString()} / {threshold.toLocaleString()}
            </span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-stone-200">
            <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        {mode === "timed" && (
          <div className={`flex items-center gap-1.5 rounded-2xl border px-4 py-2 ${timeLeft <= 10 ? "border-red-200 bg-red-50 text-red-600" : "border-stone-200 bg-white text-stone-700"}`} data-testid="beematch-timer">
            <Timer className="h-4 w-4" />
            <span className="text-xl font-bold tabular-nums">{timeLeft}s</span>
          </div>
        )}
      </div>

      {/* board with honeycomb background */}
      <div className="overflow-x-auto pb-1">
        <div className="relative rounded-2xl border border-amber-200/70 bg-amber-50/40 p-3" style={{ width: boardPx + 24 }}>
          <svg className="pointer-events-none absolute inset-3 rounded-xl" width={boardPx} height={boardPx} aria-hidden="true">
            {hexes.map((pts, i) => (
              <polygon key={i} points={pts} fill="none" stroke="rgba(202,138,4,0.16)" strokeWidth="2" />
            ))}
          </svg>

          <div
            ref={gridRef}
            className="relative grid select-none"
            style={{ gridTemplateColumns: `repeat(${COLS}, ${CELL}px)`, gap: GAP, touchAction: "none" }}
            data-testid="beematch-board"
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={() => {
              dragRef.current = null;
            }}
          >
            {board.map((row, r) =>
              row.map((cell, c) => {
                const isSel = selected && selected.r === r && selected.c === c;
                return (
                  <button
                    key={`${r}-${c}-${versionsRef.current[r][c]}`}
                    data-testid={`bm-cell-${r}-${c}`}
                    onPointerDown={(e) => onPointerDown(r, c, e)}
                    disabled={status !== "playing"}
                    className="rounded-2xl outline-none transition-transform"
                    style={{
                      width: CELL,
                      height: CELL,
                      transform: isSel ? "scale(1.12)" : "none",
                      boxShadow: isSel ? "0 0 0 3px rgba(245,158,11,0.9)" : "none",
                      borderRadius: 14,
                      zIndex: isSel ? 5 : 1,
                      touchAction: "none",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        animation: "bm-drop 340ms cubic-bezier(.2,.85,.3,1.25) both",
                        animationDelay: `${(ROWS - 1 - r) * 14}ms`,
                      }}
                    >
                      <Gem type={cell.t} special={cell.s} />
                    </div>
                  </button>
                );
              }),
            )}
          </div>

          {/* win / lose overlay */}
          {status !== "playing" && (
            <div className="absolute inset-0 z-20 grid place-items-center rounded-2xl bg-white/85 backdrop-blur-sm" data-testid="beematch-overlay">
              <div className="max-w-xs rounded-2xl border border-amber-200 bg-white px-6 py-5 text-center shadow-xl">
                <div className="mx-auto mb-2 grid h-14 w-14 place-items-center rounded-full bg-amber-100">
                  {status === "won" ? <Trophy className="h-7 w-7 text-amber-500" /> : <Timer className="h-7 w-7 text-red-500" />}
                </div>
                <h2 className="font-display text-2xl font-semibold">{status === "won" ? `Level ${level} cleared!` : "Time's up!"}</h2>
                <p className="mt-1 text-sm text-stone-500">
                  {status === "won"
                    ? `You hit ${score.toLocaleString()} points. 🍯`
                    : `You reached ${score.toLocaleString()} of ${threshold.toLocaleString()}. So close!`}
                </p>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={resetLevel}>
                    {status === "won" ? "Replay" : "Try again"}
                  </Button>
                  {status === "won" && (
                    <Button className="flex-1" onClick={nextLevel} data-testid="beematch-next">
                      Next <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-stone-400">
        Best level {bestLevel} · High score {highScore.toLocaleString()} · {wins} levels cleared
      </p>

      {/* help */}
      {showHelp && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setShowHelp(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold">How to play</h2>
              <button onClick={() => setShowHelp(false)} className="text-stone-400 hover:text-stone-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="list-disc space-y-2 pl-5 text-sm text-stone-600">
              <li><b>Drag</b> a piece onto a neighbour — or tap it, then tap the neighbour — to swap.</li>
              <li>Line up <b>3 or more</b> of the same kind to clear them and score points. Chains score more!</li>
              <li>Match <b>4</b> to forge a <b>Line Blaster</b> <Sparkles className="inline h-3.5 w-3.5 text-amber-500" /> — it clears the whole row or column, matching the direction you lined it up.</li>
              <li>Make an <b>L or T shape</b> (a row and a column together) to forge a <b>Cross Blaster</b> — it clears its whole row <b>and</b> column.</li>
              <li>Match <b>5</b> to forge a <b>Colour Bomb</b> — swap it with any tile to clear <b>every tile of that colour</b>.</li>
              <li>Swap two power pieces together for a <b>combo</b>: two blasters clear 3 rows + 3 columns; a bomb + blaster blasts each tile of that colour's row &amp; column; two bombs clear the whole board for bonus points!</li>
              <li>Reach the <b>target score</b> to complete the level.</li>
              <li><b>Casual</b> has no clock; <b>Timed</b> gives you {timeForLevel()} seconds. Progress saves automatically.</li>
            </ul>
            <Button className="mt-5 w-full" onClick={() => setShowHelp(false)}>Got it</Button>
          </div>
        </div>
      )}
    </div>
  );
}
